import { cva, type VariantProps } from 'class-variance-authority'
import type * as React from 'react'
import { cn } from '@/lib/utils'

/**
 * Status, in one word and one colour.
 *
 * `live` is its own tone rather than `critical` reused: a call in progress is
 * not an error, and this badge sits on screen for the whole call.
 */
const badge = cva(
  'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium whitespace-nowrap',
  {
    variants: {
      tone: {
        neutral: 'border-line bg-surface-2 text-ink-muted',
        accent: 'border-accent-line bg-accent-soft text-accent',
        live: 'border-live/30 bg-live-soft text-live',
        ok: 'border-ok/25 bg-ok-soft text-ok',
        warn: 'border-warn/25 bg-warn-soft text-warn',
        critical: 'border-critical/25 bg-critical-soft text-critical',
      },
    },
    defaultVariants: { tone: 'neutral' },
  },
)

export type BadgeProps = React.HTMLAttributes<HTMLSpanElement> & VariantProps<typeof badge>

export function Badge({ className, tone, ...props }: BadgeProps) {
  return <span className={cn(badge({ tone }), className)} {...props} />
}

/** The recording dot. The only ambient motion in the app. */
export function LiveDot({ className }: { className?: string }) {
  return (
    <span
      aria-hidden
      className={cn('pulse-live inline-block size-1.5 rounded-full bg-live', className)}
    />
  )
}
